import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllBanquets,
  getSingleBanquet,
  createBanquet,
  updateBanquet,
  deleteBanquet,
} from "@/api/new-api/staff-management/menu-manage/special-menu/banquet";

// Thunks

export const fetchAllBanquets = createAsyncThunk(
  "banquets/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllBanquets(restaurant_id);
    if (res.status === "success") return res.data.banquets;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleBanquet = createAsyncThunk(
  "banquets/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleBanquet(id);
    if (res.status === "success") return res.data.banquet;
    return rejectWithValue(res.message);
  }
);

export const createNewBanquet = createAsyncThunk(
  "banquets/create",
  async (data, { rejectWithValue }) => {
    const res = await createBanquet(data);
    if (res.status === "success") return res.data.banquet;
    return rejectWithValue(res.message);
  }
);

export const updateExistingBanquet = createAsyncThunk(
  "banquets/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateBanquet(id, data);
    if (res.status === "success") return res.data.banquet;
    return rejectWithValue(res.message);
  }
);

export const deleteBanquetById = createAsyncThunk(
  "banquets/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteBanquet(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State
const initialState = {
  banquets: [],
  singleBanquet: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const banquetSlice = createSlice({
  name: "banquets",
  initialState,
  reducers: {
    clearBanquetMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllBanquets.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllBanquets.fulfilled, (state, action) => {
        state.getLoading = false;
        state.banquets = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllBanquets.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleBanquet.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleBanquet.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleBanquet = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleBanquet.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewBanquet.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewBanquet.fulfilled, (state, action) => {
        state.createLoading = false;
        state.banquets.push(action.payload);
        state.success = "Banquet created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewBanquet.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingBanquet.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingBanquet.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.banquets = state.banquets.map((banquet) =>
          banquet.id === action.payload.id ? action.payload : banquet
        );
        state.success = "Banquet updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingBanquet.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteBanquetById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteBanquetById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.banquets = state.banquets.filter(
          (banquet) => banquet.id !== action.payload
        );
        state.success = "Banquet deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteBanquetById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearBanquetMessages } = banquetSlice.actions;
export default banquetSlice.reducer;
